// Purpose: SPA-local audit verb catalog + UI grouping for the multi-select
// filter in admin/Audit.tsx and the friendly label mapping used by the
// audit list + drill-down.
//
// Canonical source: packages/shared/src/audit-actions.ts (server-side
// AuditAction const). The SPA can't `import` from @ast/shared because
// the barrel re-exports @prisma/client which can't ship to the browser.
// Adding a server-side category metadata field would mix UI concerns into
// a hashed-payload constant — keep the SPA list parallel and review on
// every audit-actions.ts change.
//
// When you add a new verb to audit-actions.ts, also:
//   1. Add it to one of the CATEGORIES below.
//   2. Add an entry to ACTION_LABELS if the raw verb isn't readable.
// The "find every verb in CATEGORIES" assertion in admin/Audit.tsx (or
// a unit test if added later) keeps drift visible.

export type VerbCategory =
  | 'Authentication'
  | 'Share Lifecycle'
  | 'Dropbox Lifecycle'
  | 'Recipient Notifications'
  | 'Recipient Access'
  | 'Routing'
  | 'Crypto + KEK'
  | 'Admin Actions'
  | 'Break-glass'
  | 'System';

export interface VerbEntry {
  verb: string;
  label: string;
}

export const CATEGORIES: Record<VerbCategory, VerbEntry[]> = {
  Authentication: [
    { verb: 'auth.login.initiated', label: 'Login initiated' },
    { verb: 'auth.login.succeeded', label: 'Login succeeded' },
    { verb: 'auth.systemuse.acknowledged', label: 'System-use notice acknowledged' },
    { verb: 'auth.login.failed', label: 'Login failed' },
    { verb: 'auth.logout', label: 'Logout' },
    { verb: 'auth.session.expired', label: 'Session expired' },
    { verb: 'auth.session.revoked', label: 'Session revoked' },
    { verb: 'auth.fingerprint.ip_changed', label: 'Session IP changed' },
    { verb: 'auth.fingerprint.ua_changed', label: 'Session UA changed' },
    { verb: 'auth.user.provisioned', label: 'User provisioned' },
    { verb: 'auth.user.role_changed', label: 'User role changed' },
    { verb: 'auth.mfa.satisfied', label: 'MFA satisfied' },
    { verb: 'auth.mfa.step_up_failed', label: 'MFA step-up failed' },
  ],
  'Share Lifecycle': [
    { verb: 'share.draft.created', label: 'Share draft created' },
    { verb: 'share.draft.abandoned', label: 'Share draft abandoned' },
    { verb: 'share.file.uploaded', label: 'Share file uploaded' },
    { verb: 'share.created', label: 'Share created' },
    { verb: 'share.viewed_by_sender', label: 'Share viewed by sender' },
    { verb: 'share.code.verified', label: 'Share code verified' },
    { verb: 'share.code.failed', label: 'Share code failed' },
    { verb: 'share.locked', label: 'Share locked (recipient lockout)' },
    { verb: 'share.file.downloaded', label: 'Share file downloaded' },
    { verb: 'share.zip.downloaded', label: 'Share ZIP downloaded' },
    { verb: 'share.downloaded', label: 'Share downloaded' },
    { verb: 'share.revoked', label: 'Share revoked' },
    { verb: 'share.expired', label: 'Share expired' },
    { verb: 'share.revoked.blobs_purged', label: 'Revoked share files purged' },
    { verb: 'share.expired.blobs_purged', label: 'Expired share files purged' },
    { verb: 'share.extended', label: 'Share TTL extended' },
    { verb: 'share.link.recovered', label: 'Owner recovered share link' },
    { verb: 'share.archived', label: 'Share archived (owner / auto)' },
    { verb: 'share.unarchived', label: 'Share unarchived (owner)' },
  ],
  'Dropbox Lifecycle': [
    { verb: 'dropbox.created', label: 'Dropbox created' },
    { verb: 'dropbox.rotated', label: 'Dropbox token rotated' },
    { verb: 'dropbox.deactivated', label: 'Dropbox deactivated' },
    { verb: 'dropbox.revoked', label: 'Dropbox revoked' },
    { verb: 'dropbox.expired', label: 'Dropbox expired' },
    { verb: 'dropbox.approval.requested', label: 'Dropbox approval requested' },
    { verb: 'dropbox.approval.granted', label: 'Dropbox approval granted' },
    { verb: 'dropbox.approval.approved', label: 'Dropbox approval approved' },
    { verb: 'dropbox.approval.denied', label: 'Dropbox approval denied' },
    { verb: 'dropbox.upload.received', label: 'Dropbox upload received' },
    { verb: 'dropbox.upload.infected', label: 'Dropbox upload infected' },
    { verb: 'dropbox.file.retrieved', label: 'Dropbox file retrieved' },
    { verb: 'dropbox.geo.evaluated', label: 'Dropbox geo evaluated' },
    { verb: 'dropbox.geo.rejected', label: 'Dropbox geo rejected' },
    { verb: 'dropbox.geo.anomaly', label: 'Dropbox geo anomaly' },
    { verb: 'dropbox.link.recovered', label: 'Owner recovered dropbox link' },
    { verb: 'dropbox.expired.blobs_purged', label: 'Expired persistent dropbox files purged' },
    {
      verb: 'dropbox.routing.rejected_destination',
      label: 'Dropbox routing destination refused (not authorized)',
    },
  ],
  'Recipient Notifications': [
    { verb: 'recipient.notification.link_sent', label: 'Recipient notification: link sent' },
    { verb: 'recipient.notification.code_sent', label: 'Recipient notification: code sent' },
    { verb: 'recipient.notification.totp_sent', label: 'Recipient notification: TOTP sent' },
    { verb: 'recipient.notification.failed', label: 'Recipient notification: FAILED' },
  ],
  'Recipient Access': [
    { verb: 'share.recipients.created', label: 'Share recipients created' },
    { verb: 'recipient.email_validated', label: 'Recipient: email validated (step 1)' },
    { verb: 'recipient.email_verification.failed', label: 'Recipient: email verification failed' },
    { verb: 'recipient.access_code_verified', label: 'Recipient: access code verified (step 2)' },
    { verb: 'recipient.access.failed', label: 'Recipient: access code failed' },
    { verb: 'recipient.totp.verified', label: 'Recipient: TOTP verified (step 3 / full)' },
    { verb: 'recipient.totp.failed', label: 'Recipient: TOTP failed' },
    { verb: 'recipient.access.granted', label: 'Recipient: access granted (download)' },
    { verb: 'recipient.locked_out', label: 'Recipient: locked out' },
    { verb: 'recipient.revoked', label: 'Recipient revoked (owner)' },
    { verb: 'share.codes_recovered', label: 'Share codes recovered (owner MFA)' },
    { verb: 'share.recipient.backfill_skipped', label: '8b.14 backfill: share skipped' },
  ],
  Routing: [
    { verb: 'upload.started', label: 'Upload started' },
    { verb: 'upload.completed', label: 'Upload completed' },
    { verb: 'upload.scan.completed', label: 'Upload scan completed' },
    { verb: 'upload.infected', label: 'Upload infected' },
    { verb: 'upload.routed', label: 'Upload routed' },
    { verb: 'upload.consumed', label: 'Upload consumed' },
    { verb: 'upload.expired', label: 'Upload expired' },
    { verb: 'upload.routing.enqueued', label: 'Routing enqueued' },
    { verb: 'upload.routing.started', label: 'Routing started' },
    { verb: 'upload.routing.progress', label: 'Routing progress' },
    { verb: 'upload.routing.retrying', label: 'Routing retrying' },
    { verb: 'upload.routing.failed', label: 'Routing failed' },
    { verb: 'upload.routing.dead_lettered', label: 'Routing dead-lettered' },
    { verb: 'upload.failed', label: 'Upload failed (PUT error)' },
    { verb: 'notification.sent', label: 'Workflow notification sent' },
    { verb: 'notification.failed', label: 'Workflow notification failed' },
  ],
  'Crypto + KEK': [
    { verb: 'crypto.dek.generated', label: 'DEK generated' },
    { verb: 'crypto.wrap', label: 'DEK wrapped' },
    { verb: 'crypto.unwrap', label: 'DEK unwrapped' },
    { verb: 'crypto.format.error', label: 'Crypto format error' },
    { verb: 'kek.rotation.started', label: 'KEK rotation started' },
    { verb: 'kek.rotation.progress', label: 'KEK rotation progress' },
    { verb: 'kek.rotation.completed', label: 'KEK rotation completed' },
    { verb: 'kek.version.retired', label: 'KEK version retired' },
  ],
  'Admin Actions': [
    { verb: 'admin.approval.bulk_approved', label: 'Bulk approval: approved' },
    { verb: 'admin.approval.bulk_denied', label: 'Bulk approval: denied' },
    { verb: 'admin.user.role_changed', label: 'Admin changed user role' },
    { verb: 'admin.user.deactivated', label: 'Admin deactivated user' },
    { verb: 'admin.user.reactivated', label: 'Admin reactivated user' },
    { verb: 'admin.setting.changed', label: 'Admin changed setting' },
    { verb: 'admin.feature_flag.toggled', label: 'Admin toggled feature flag' },
    { verb: 'admin.routing.replayed', label: 'Admin replayed routing job' },
    { verb: 'admin.routing.discarded', label: 'Admin discarded routing job' },
    { verb: 'admin.circuit_breaker.reset', label: 'Admin reset circuit breaker' },
    { verb: 'admin.audit.exported', label: 'Admin exported audit CSV' },
    { verb: 'admin.audit.chain_verified', label: 'Admin verified audit chain' },
    { verb: 'admin.system.sealed', label: 'Admin sealed system' },
    { verb: 'admin.graph.test_email', label: 'Admin: Graph test email' },
  ],
  'Break-glass': [
    { verb: 'admin.content.accessed', label: 'Break-glass: content accessed' },
    { verb: 'admin.forensics.access_granted', label: 'Break-glass: access granted' },
    { verb: 'admin.forensics.content_viewed', label: 'Break-glass: content viewed' },
    { verb: 'admin.forensics.content_unavailable', label: 'Break-glass: content UNAVAILABLE' },
    { verb: 'admin.forensics.self_access', label: 'Break-glass: self-access (skipped)' },
    { verb: 'admin.forensics.session_terminated', label: 'Break-glass: session terminated' },
  ],
  System: [{ verb: 'audit.genesis', label: 'Audit chain genesis' }],
};

// Flat lookup for action → friendly label. Built once at module init.
const LABEL_MAP: Record<string, string> = (() => {
  const out: Record<string, string> = {};
  for (const entries of Object.values(CATEGORIES)) {
    for (const e of entries) out[e.verb] = e.label;
  }
  return out;
})();

export function actionLabel(verb: string): string {
  return LABEL_MAP[verb] ?? verb;
}

// Flat list of all known verbs, in category order. Useful for the
// MultiSelect's group rendering.
export const ALL_VERBS: VerbEntry[] = (() => {
  const out: VerbEntry[] = [];
  for (const entries of Object.values(CATEGORIES)) out.push(...entries);
  return out;
})();
